import { Alert, Box } from '@factorialco/deprecated-design-system'
import {
  A3IncidenceFinanceCostCenterAssignmentFragment,
  IncidencePersonalHiringDetailsFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import useFetchIncidenceChangeItems from 'modules/payroll_bundle/hooks/useFetchIncidenceChangeItems'
import type { IncidenceChangeItem, IncidenceDetailsField } from 'modules/payroll_bundle/lib/types'
import { hasValidValue } from 'modules/payroll_bundle/lib/utils'
import ChangeItem from 'modules/payroll_bundle/pages/IncidenceDetailPage/_ChangedItem'
import InformationLink from 'modules/payroll_bundle/pages/IncidenceDetailPage/_MoreInformation/InformationLink'

import { CostCentersHiringDetails } from './CostCentersHiringDetails'
import HiringDetails from './HiringDetails'
import {
  AgreementInformationFields,
  InsuranceInformationFields,
  PersonalFields,
  TaxesAndDeductionsFields,
  WorkActivityInformationFields,
} from './utils'

interface Props {
  costCenters: A3IncidenceFinanceCostCenterAssignmentFragment[]
  incidenceDetailModel: IncidenceDetail
  isBookkeeperView: boolean
  personalHiringDetails: IncidencePersonalHiringDetailsFragment[]
  hasA3innuvaFeature: boolean
}

/**
 * Note: When we convert this component to use GraphQL we should also switch
 * to display not compensation changes, but the employee's compensations for their
 * respective contract version. The current approach has holes for different edge cases
 * that could create visibility in niche situations.
 */
const HiringInfo = ({
  costCenters,
  incidenceDetailModel,
  isBookkeeperView = false,
  personalHiringDetails,
  hasA3innuvaFeature,
}: Props) => {
  const { navigation } = useNavigation()

  const incidence = useCurrentIncidence()
  const employeeId = incidence.employeeId ?? 0
  const companyId = incidence.legalEntity?.companyId

  const showA3nomError =
    isBookkeeperView &&
    hasA3innuvaFeature &&
    !incidenceDetailModel.attributes?.payroll_a3nom_integration_code

  const { loading, changeItems } = useFetchIncidenceChangeItems(
    incidence.id,
    isBookkeeperView,
    incidence?.name ?? null,
    incidence?.targetId ?? null,
    incidence
  )

  const showHiringInfoSection = (hiringFields: IncidenceDetailsField[]) =>
    hiringFields.some((hiringField) =>
      hasValidValue(incidenceDetailModel.attributes[hiringField.field])
    )

  return (
    <Box gap="s16">
      {showA3nomError && (
        <Alert color="warning">
          <Box flexDirection="column" gap="s8">
            {i18n.t('bookkeepers.incidence.details.a3nom_error')}
            <InformationLink
              href={`${navigation.employees.id.personal.fullPath({
                id: employeeId,
              })}?switchToCompanyId=${companyId}`}
              label={i18n.t('bookkeepers.incidence.details.see_employee_profile')}
            />
          </Box>
        </Alert>
      )}
      {showHiringInfoSection(PersonalFields) && (
        <HiringDetails
          incidenceDetailModel={incidenceDetailModel}
          title={i18n.t('bookkeepers.incidence.details.sections.personal_information')}
          hiringFields={PersonalFields}
          personalHiringDetails={personalHiringDetails}
        />
      )}
      {showHiringInfoSection(AgreementInformationFields) && (
        <HiringDetails
          incidenceDetailModel={incidenceDetailModel}
          title={i18n.t('bookkeepers.incidence.details.sections.agreement_information')}
          hiringFields={AgreementInformationFields}
        />
      )}
      {showHiringInfoSection(InsuranceInformationFields) && (
        <HiringDetails
          incidenceDetailModel={incidenceDetailModel}
          title={i18n.t('bookkeepers.incidence.details.sections.insurance_information')}
          hiringFields={InsuranceInformationFields}
        />
      )}
      {showHiringInfoSection(TaxesAndDeductionsFields) && (
        <HiringDetails
          incidenceDetailModel={incidenceDetailModel}
          title={i18n.t('bookkeepers.incidence.details.sections.taxes_and_deductions_information')}
          hiringFields={TaxesAndDeductionsFields}
        />
      )}
      {showHiringInfoSection(WorkActivityInformationFields) && (
        <HiringDetails
          incidenceDetailModel={incidenceDetailModel}
          title={i18n.t('bookkeepers.incidence.details.sections.work_activity_information')}
          hiringFields={WorkActivityInformationFields}
        />
      )}
      {costCenters.length > 0 && (
        <CostCentersHiringDetails
          title={i18n.t('bookkeepers.incidence.details.sections.cost_centers')}
          costCenters={costCenters}
        />
      )}
      {incidenceDetailModel.attributes.contract_version_id && !loading ? (
        <Box gap="s2">
          {changeItems.reverse().map((change: IncidenceChangeItem, index: number) => {
            return <ChangeItem key={`${index}-${change.id}`} change={change} />
          })}
        </Box>
      ) : null}
    </Box>
  )
}

export default HiringInfo
